import { useCallback, useEffect, useState } from "react";
import { Client } from "../../core/api-client";
import { isFailedResponse } from "./dashboard-response";

export const missingStaffFields: Record<string, { label: string; tab: "personal" | "job"; section: string }> = {
  email: { label: "Email", tab: "personal", section: "contact" },
  role: { label: "Role", tab: "job", section: "work" },
  lineManagerId: { label: "Line Manager", tab: "job", section: "work" },
   // Contract details
  contractStartDate: {
    label: 'Contract Start Date',
    tab: 'job',
    section: 'work',
  },
  contractEndDate: {
    label: 'Contract End Date',
    tab: 'job',
    section: 'work',
  },

  // Probation details
  probationEndDate: {
    label: 'Probation End Date',
    tab: 'job',
    section: 'work',
  },

  // Performance and appraisal details
  nextPerformanceReviewDate: {
    label: 'Next Performance Review Date',
    tab: 'job',
    section: 'work',
  },
  nextAppraisalSalaryRevisionDate: {
    label: 'Next Appraisal/Salary Revision Date',
    tab: 'job',
    section: 'work',
  },
};

export type MissingFieldSummary = {
  field: string;
  label: string;
  total: number;
  filled: number;
  completed: number;
  missing: number;
};

export function useMissingFieldsResource<T>(url: string, enabled: boolean, refreshKey = 0) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    window.addEventListener("rp:staff-updated", reload);
    return () => {
      window.removeEventListener("rp:staff-updated", reload);
    };
  }, [reload]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    // Debounce searching and ignore responses for filters that have changed.
    const timer = window.setTimeout(async () => {
      try {
        const response = await Client.get(url, { shouldHide: true });
        if (isFailedResponse(response) || response?.data?.success === false || response?.data?.data == null) throw new Error("Unable to load missing fields");
        if (!cancelled) setData(response.data.data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [url, enabled, refreshKey, version]);

  return { data, loading, error, reload };
}
